import React, { useCallback, useRef, useState } from 'react';
import { ShoppingCart, Check } from 'lucide-react';

/**
 * AddToCartButton — 3-state morph button per brand spec:
 *
 *   idle      → label + cart icon (upright)
 *   loading   → cart icon tilts/wobbles for ~600 ms (items "falling in")
 *   success   → button morphs to green, label remains, icon swaps to check
 *               (stays for ~1100 ms, then resets back to idle)
 *
 * The provided `onAdd` is called once when the user clicks. It may be sync
 * or return a Promise; the button waits for it to resolve before showing
 * the success state.
 *
 *   <AddToCartButton onAdd={() => addToCart(item)} className="..." />
 *
 * Tone overrides:
 *   variant="primary" (default cyan)
 *   variant="pink", "yellow", or pass a custom `className` for full control.
 */

const VARIANT_CLASS = {
  primary: 'bg-[var(--bb-darkblue)] text-white hover:bg-[#0e2a4f]',
  cyan: 'bg-[var(--bb-cyan)] text-white hover:bg-[var(--bb-cyan-deep)]',
  pink: 'bg-[var(--bb-pink)] text-white hover:bg-[var(--bb-pink-deep)]',
  yellow: 'bg-[var(--bb-yellow)] text-[var(--bb-darkblue)] hover:bg-[#e6ac00]',
  white: 'bg-white text-[var(--bb-darkblue)] hover:bg-white/95',
};

const SUCCESS_BG = '#0e6b3a'; // brand-friendly dark green

export const AddToCartButton = ({
  onAdd,
  label = 'Add to Cart',
  variant = 'primary',
  className = '',
  successLabel,
  ...rest
}) => {
  const [state, setState] = useState('idle'); // 'idle' | 'loading' | 'success'
  const timerRef = useRef(null);

  const handleClick = useCallback(async () => {
    if (state !== 'idle') return;
    setState('loading');
    try {
      const result = onAdd?.();
      // Show the wobble for at least ~450 ms even if onAdd is synchronous
      await Promise.all([
        Promise.resolve(result),
        new Promise((r) => {
          timerRef.current = setTimeout(r, 450);
        }),
      ]);
      setState('success');
      timerRef.current = setTimeout(() => setState('idle'), 1100);
    } catch (e) {
      setState('idle');
    }
  }, [onAdd, state]);

  const isLoading = state === 'loading';
  const isSuccess = state === 'success';
  const baseTone = isSuccess ? '' : VARIANT_CLASS[variant] || VARIANT_CLASS.primary;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={state !== 'idle'}
      data-state={state}
      className={[
        'relative inline-flex items-center justify-center gap-2 rounded-2xl px-5 h-12 font-nunito-black text-base overflow-hidden',
        'transition-[background-color,color,transform] duration-300 ease-out',
        'disabled:cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--bb-yellow)]',
        baseTone,
        'bb-press-pop',
        className,
      ].join(' ')}
      style={{
        backgroundColor: isSuccess ? SUCCESS_BG : undefined,
        color: isSuccess ? '#ffffff' : undefined,
      }}
      {...rest}
    >
      <span
        className={[
          'inline-flex items-center transition-all duration-300',
          isLoading ? 'translate-y-0' : '',
        ].join(' ')}
        aria-live="polite"
      >
        {isSuccess ? (successLabel || 'Added!') : label}
      </span>

      {/* Icon container — swaps between cart (idle/loading) and check (success) */}
      <span className="relative inline-flex w-5 h-5 items-center justify-center">
        {/* Cart icon — visible idle + loading, wobbles while loading */}
        <ShoppingCart
          aria-hidden="true"
          className={[
            'absolute w-5 h-5 transition-all duration-300',
            isSuccess ? 'opacity-0 scale-50' : 'opacity-100 scale-100',
            isLoading ? 'bb-anim-cart-bump' : '',
          ].join(' ')}
        />
        {/* Check icon — appears on success */}
        <Check
          aria-hidden="true"
          className={[
            'absolute w-5 h-5 transition-all duration-300',
            isSuccess ? 'opacity-100 scale-100 bb-anim-bounce-in' : 'opacity-0 scale-50',
          ].join(' ')}
        />
      </span>
    </button>
  );
};

export default AddToCartButton;
